import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Payments as PaymentsIcon,
  Badge as BadgeIcon,
  AccessTime,
} from '@mui/icons-material';
import { hrSummary } from '../BaseURL';

const formatCurrency = (n, ccy = 'GHS') =>
  `${ccy} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ title, value, icon: Icon, gradient, accent, loading }) => (
  <Card sx={{
    background: 'white',
    border: '1px solid #e8f5e9',
    borderRadius: 2.5,
    boxShadow: '0 2px 10px rgba(17, 153, 142, 0.06)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0, top: 0, bottom: 0,
      width: 4,
      background: gradient,
    },
    '&:hover': {
      boxShadow: '0 6px 20px rgba(17, 153, 142, 0.15)',
      transform: 'translateY(-2px)',
      borderColor: '#c8e6c9',
    },
  }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.75 }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        background: gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        boxShadow: `0 4px 10px ${accent}40`,
      }}>
        <Icon sx={{ fontSize: 22, color: 'white' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: '#607d8b', mb: 0.25,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1b5e20', lineHeight: 1.1 }}>
          {loading ? <CircularProgress size={20} sx={{ color: '#11998e' }} /> : value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total: 0, active: 0, inactive: 0,
    monthlyGross: 0, newHiresThisMonth: 0, byDepartment: [],
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(hrSummary);
        if (alive && res.data?.success) setSummary(res.data.summary);
      } catch (err) {
        console.error('Failed to load HR summary', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
      p: 2.5,
    }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5, p: 2,
          borderRadius: 2.5,
          background: 'white',
          border: '1px solid #c8e6c9',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(17, 153, 142, 0.35)',
          }}>
            <BadgeIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text', lineHeight: 1.1,
            }}>
              Human Resources
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.25 }}>
              Employees, payroll, and workforce overview
            </Typography>
          </Box>
        </Box>
        <Chip
          label={new Date().toLocaleDateString('en-US', {
            weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
          })}
          icon={<AccessTime sx={{ fontSize: 16, color: '#11998e !important' }} />}
          sx={{
            bgcolor: '#e8f5e9', color: '#1b5e20',
            fontWeight: 600, border: '1px solid #c8e6c9', borderRadius: 1.5,
          }}
        />
      </Paper>

      {/* Stat cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2, mb: 2.5,
      }}>
        <StatCard
          title="Active Employees"
          value={summary.active}
          icon={GroupIcon}
          accent="#0e8679"
          gradient="linear-gradient(135deg, #0e8679 0%, #11998e 100%)"
          loading={loading}
        />
        <StatCard
          title="New Hires (This Month)"
          value={summary.newHiresThisMonth}
          icon={PersonAddIcon}
          accent="#11998e"
          gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
          loading={loading}
        />
        <StatCard
          title="Monthly Payroll Due"
          value={formatCurrency(summary.monthlyGross)}
          icon={PaymentsIcon}
          accent="#2ed968"
          gradient="linear-gradient(135deg, #38ef7d 0%, #56ab2f 100%)"
          loading={loading}
        />
        <StatCard
          title="Inactive / Past Staff"
          value={summary.inactive}
          icon={GroupIcon}
          accent="#607d8b"
          gradient="linear-gradient(135deg, #607d8b 0%, #90a4ae 100%)"
          loading={loading}
        />
      </Box>

      {/* Department breakdown */}
      <Paper sx={{
        p: 2,
        borderRadius: 2.5,
        background: 'white',
        border: '1px solid #e8f5e9',
        boxShadow: '0 2px 12px rgba(17, 153, 142, 0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(17, 153, 142, 0.35)',
          }}>
            <GroupIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20', lineHeight: 1.2 }}>
              Headcount by Department
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active employees grouped by their assigned department
            </Typography>
          </Box>
        </Box>

        <TableContainer sx={{ borderRadius: 2, border: '1px solid #e8f5e9', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Department</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Headcount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: '#11998e' }} />
                  </TableCell>
                </TableRow>
              ) : summary.byDepartment.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                    <Box sx={{
                      textAlign: 'center', py: 5,
                      background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
                      border: '1px dashed #c8e6c9',
                    }}>
                      <GroupIcon sx={{ fontSize: 44, color: '#11998e', opacity: 0.5, mb: 1 }} />
                      <Typography variant="subtitle1" sx={{ color: '#1b5e20', fontWeight: 600 }}>
                        No employees yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add your first employee from the Employees tab
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                summary.byDepartment.map((d, i) => (
                  <TableRow key={d.department} sx={{
                    bgcolor: i % 2 === 0 ? 'white' : '#f8fdfb',
                    '&:hover': { bgcolor: '#e8f5e9' },
                  }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1b5e20' }}>
                      {d.department}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={d.count}
                        sx={{
                          bgcolor: '#e8f5e9', color: '#1b5e20',
                          fontWeight: 700, border: '1px solid #c8e6c9',
                          minWidth: 48,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Home;
